import React from "react";
import { formatUserHandle } from "../../../utils/userHandle";
import type { AdminSeason } from "../api/types";
import { fmtDay, fmtMoment, PHASE_LABEL } from "./adminText";
import { Grid, Kicker, Pill, Section, SectionTitle, Small, Tile, Value } from "./adminStyles";

function dayLine(s: AdminSeason): string {
  if (s.phase === "active" && s.dayNumber) return `Day ${s.dayNumber} of ${s.totalDays}`;
  if (s.phase === "ended" || s.phase === "archived") return `Over (${s.totalDays} days)`;
  return `Not started (${s.totalDays} days)`;
}

const SeasonCard: React.FC<{ season: AdminSeason }> = ({ season: s }) => {
  const { frames } = s;
  return (
    <Section aria-label="Season">
      <SectionTitle>{s.name}</SectionTitle>
      {s.description && <Small style={{ marginBottom: 12 }}>{s.description}</Small>}
      <Grid>
        <Tile>
          <Kicker>Status</Kicker>
          <Value data-testid="gadm-phase">{PHASE_LABEL[s.phase] ?? s.phase}</Value>
          <Small>
            stored <code>{s.status}</code>
            {s.joinOpen ? ", joining open" : ", joining closed"}
          </Small>
        </Tile>
        <Tile>
          <Kicker>Dates</Kicker>
          <Value>
            {fmtDay(s.dayKeyFrom)} to {fmtDay(s.dayKeyTo)}
          </Value>
          <Small>
            {s.opensAt ? `joining opens ${fmtMoment(s.opensAt)}` : "no opening time set"}
            {s.datesLocked ? ", dates locked" : ""}
          </Small>
        </Tile>
        <Tile>
          <Kicker>Day</Kicker>
          <Value data-testid="gadm-day">{dayLine(s)}</Value>
          <Small>
            {s.daysLeft} {s.daysLeft === 1 ? "day" : "days"} left
          </Small>
        </Tile>
        <Tile>
          <Kicker>Players</Kicker>
          <Value data-testid="gadm-players">{s.participantCount}</Value>
          <Small>{s.countDaysBeforeJoin ? "late joiners keep earlier days" : "only days after joining count"}</Small>
        </Tile>
        <Tile>
          <Kicker>Last settled day</Kicker>
          <Value>{s.lastSettledDayKey ? fmtDay(s.lastSettledDayKey) : "none yet"}</Value>
          <Small>
            {s.settledDays} settled, {s.finalDays} final
            {s.lastFinalDayKey ? ` (last final ${fmtDay(s.lastFinalDayKey)})` : ""}
          </Small>
        </Tile>
        <Tile>
          <Kicker>Teasers</Kicker>
          <Small>3 days before: {s.teasers.d3At ? `sent ${fmtMoment(s.teasers.d3At)}` : "not sent"}</Small>
          <Small>1 day before: {s.teasers.d1At ? `sent ${fmtMoment(s.teasers.d1At)}` : "not sent"}</Small>
        </Tile>
      </Grid>

      <Tile style={{ marginTop: 12 }} aria-label="Frames">
        <Kicker>
          AOTD IRL frames {frames.year}{" "}
          {frames.provisional && <Pill tone="warn">provisional until 31 December</Pill>}
        </Kicker>
        <Small>From the AOTD leaderboard top 3:</Small>
        {frames.leaderboardTop.length === 0 ? (
          <Small>nobody yet</Small>
        ) : (
          <ol style={{ margin: "4px 0 8px 18px", listStyle: "decimal" }}>
            {frames.leaderboardTop.map((p) => (
              <li key={p.username} style={{ fontSize: "0.9rem" }}>
                {formatUserHandle(p.username, p.emoji)}
                {p.inGame ? <Small as="span"> (also playing)</Small> : null}
              </li>
            ))}
          </ol>
        )}
        <Small>
          The game's frame:{" "}
          <strong data-testid="gadm-game-taker">
            {frames.gameTaker ? formatUserHandle(frames.gameTaker.username, frames.gameTaker.emoji) : "nobody yet"}
          </strong>
        </Small>
        <Small>Prize text: {s.prizeText || "none"}</Small>
      </Tile>
    </Section>
  );
};

export default SeasonCard;
